import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BookOpen, Award, Users, TrendingUp } from 'lucide-react';
import Logo from '../components/Logo';
import CourseCard from '../components/CourseCard';
import { courses } from '../lib/api';
import { isAuthenticated } from '../lib/auth';

const LandingPage = () => {
  const navigate = useNavigate();
  const [courseList, setCourseList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCourses();
  }, []);

  const loadCourses = async () => {
    try {
      const response = await courses.getAll();
      setCourseList(response.data);
    } catch (error) {
      console.error('Failed to load courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGetStarted = () => {
    if (isAuthenticated()) {
      navigate('/courses');
    } else {
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-50">
      <nav className="border-b border-gray-200 bg-white/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
          <div className="flex items-center justify-between">
            <Logo size="sm" />
            <div className="flex items-center gap-4">
              {isAuthenticated() ? (
                <>
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="px-6 py-2 text-gray-700 hover:text-gray-900 font-medium transition-colors"
                    data-testid="dashboard-nav-btn"
                  >
                    Dashboard
                  </button>
                  <button
                    onClick={() => navigate('/courses')}
                    className="px-6 py-2 bg-black text-white rounded-lg font-medium hover:bg-gray-800 transition-colors"
                    data-testid="courses-nav-btn"
                  >
                    Browse Courses
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => navigate('/login')}
                    className="px-6 py-2 text-gray-700 hover:text-gray-900 font-medium transition-colors"
                    data-testid="login-nav-btn"
                  >
                    Login
                  </button>
                  <button
                    onClick={() => navigate('/register')}
                    className="px-6 py-2 bg-black text-white rounded-lg font-medium hover:bg-gray-800 transition-colors"
                    data-testid="register-nav-btn"
                  >
                    Sign Up
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      <section className="py-10 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-[34px] font-bold tracking-tight leading-none text-gray-900 mb-6">
                Learn German,
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-gray-900 to-gray-600">
                  Pay with Pi
                </span>
              </h1>
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-8">
                Master the German language from A1 to C1 levels. Pay securely with Pi Network cryptocurrency.
                Professional instruction, flexible schedules, verified certification.
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleGetStarted}
                className="px-8 py-4 bg-black text-white rounded-xl font-semibold text-lg hover:bg-gray-800 transition-colors shadow-xl"
                data-testid="get-started-btn"
              >
                Get Started
              </motion.button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1632464870080-194217250bbc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxCZXJsaW4lMjBsYW5kbWFya3MlMjBzdHVkZW50c3xlbnwwfHx8fDE3NjY1NzkyOTJ8MA&ixlib=rb-4.1.0&q=85"
                alt="Berlin landmarks"
                className="rounded-3xl shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { icon: BookOpen, title: '6 Levels', desc: 'A1 to C1' },
              { icon: Users, title: 'Expert Teachers', desc: 'Native speakers' },
              { icon: Award, title: 'Certification', desc: 'Verified' },
              { icon: TrendingUp, title: 'Track Progress', desc: 'Real-time' },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center p-6"
              >
                <div className="w-16 h-16 mx-auto mb-4 bg-black rounded-2xl flex items-center justify-center">
                  <item.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32" id="courses">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Our Courses</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Choose your level and start your German learning journey
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block w-12 h-12 border-4 border-gray-300 border-t-black rounded-full animate-spin" />
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {courseList.map((course, index) => (
                <motion.div
                  key={course.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <CourseCard course={course} onClick={() => navigate(`/courses/${course.id}`)} />
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-24 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">How Pi Payment Works</h2>
            <p className="text-gray-300 text-lg">Simple and secure cryptocurrency payments</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '1', title: 'Choose Course', desc: 'Select your desired German language level' },
              { step: '2', title: 'Scan QR Code', desc: 'Pay with your Pi network app by scanning our Pi wallet QR code. If you have not installed your Pi App yet,', link: 'https://minepi.com', referralCode: 'P2Y2' },
              { step: '3', title: 'Start Learning', desc: 'Access course materials after verification' },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white/10 backdrop-blur-xl rounded-2xl p-8 border border-white/20"
              >
                <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center text-2xl font-bold mb-4">
                  {item.step}
                </div>
                <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                <p className="text-gray-300">
                  {item.desc}
                  {item.link && (
                    <a href={item.link} target="_blank" rel="noopener noreferrer" className="text-red-400 hover:text-red-300 underline ml-1" data-testid="pi-app-link">
                      click here
                    </a>
                  )}
                  {item.referralCode && (
                    <span className="block mt-3 text-white font-semibold">
                      Use the case sensitive referral code: <span className="bg-white/20 px-3 py-1 rounded-lg text-yellow-300 font-bold tracking-wider" data-testid="referral-code">{item.referralCode}</span> to earn a Bonus Pi coin
                    </span>
                  )}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <footer className="bg-black text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <Logo size="sm" className="mb-4 md:mb-0 [&_h1]:text-white [&_path]:stroke-white" />
            <p className="text-gray-400">© 2025 Pi German Academy. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
